import React, { useState, useMemo } from 'react';
import { withStyles, makeStyles } from '@material-ui/core/styles';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import { Grid, Button, Box, Paper, Typography } from "@material-ui/core";
import axios from 'axios';
import AuthService from '../../services/AuthService';
import Alert from '@material-ui/lab/Alert';
import NumberFormat from "react-number-format";

const StyledTableCell = withStyles((theme) => ({
  head: {
    backgroundColor: '#14688D',
    color: theme.palette.common.white,
    fontSize: 18,
  },
  body: { fontSize: 16 },
}))(TableCell);

const StyledTableRow = withStyles((theme) => ({
  root: {
    '&:nth-of-type(odd)': {
      backgroundColor: theme.palette.action.hover,
      marginTop: '2rem',
    },
  },
}))(TableRow);

function createData(question, answer) {
  return { question, answer };
}

const useStyles = makeStyles((theme) => ({
  table: { width: '100%', justifyContent: "center" },
  reviewButton: {
    marginTop: '1rem',
    marginBottom: '2em',
    background: '#14688D',
    color: '#fff',
    '&:hover': { backgroundColor: '#fff', color: '#14688D' }
  },
  submitButton: {
    height: '3rem',
    marginBottom: '1rem',
    background: '#14688D',
    color: '#fff',
    '&:hover': { backgroundColor: '#fff', color: '#14688D' },
  },
  container: { marginTop: '3rem', overflow: 'hidden' },
  main: { marginTop: '2rem', justifyContent: 'center' },
  hero: {
    ...theme.typography.button,
    backgroundColor: '#14688D',
    padding: theme.spacing(3),
    color: 'white',
    fontSize: '20px',
  },
  scholarshipAlert: {
    position: 'relative',
    marginBottom: '2rem',
    height: '9em',
    color: '#fff',
    textAlign: 'center',
    border: '1px solid #FF9800',
    borderRadius: '5px',
    backgroundColor: '#FF9800'
  },
  twoFiles: { color: 'red' },
  warning: { padding: '4px !important', position: 'relative' },

  // Modern upload line
  uploadCard: {
    border: '1px dashed rgba(20,104,141,0.35)',
    background: 'linear-gradient(145deg, rgba(244,249,252,0.9) 0%, rgba(233,244,250,0.9) 100%)',
    borderRadius: 12,
    padding: '14px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: 12,
  },
  fileMeta: { flex: 1, minWidth: 0 },
  fileName: {
    color: '#1c2b3a',
    fontWeight: 600,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  fileHelper: { color: '#54606c' },
  uploadActions: {
    display: 'flex',
    gap: 8,
    flexWrap: 'wrap',
  },
  uploadBtn: {
    background: '#14688D',
    color: '#fff',
    '&:hover': { background: '#fff', color: '#14688D' },
  },
  removeBtn: {
    borderColor: 'rgba(229,57,53,0.35)',
    color: '#e53935',
  },

  // Notice card (light orange)
  noticeCard: {
    borderRadius: 14,
    border: '1px solid rgba(255, 152, 0, 0.25)',
    background:
      'linear-gradient(145deg, rgba(255,243,224,0.95) 0%, rgba(255,224,178,0.95) 100%)',
    boxShadow: '0 8px 20px rgba(255, 152, 0, 0.15)',
    padding: theme.spacing(2.5),
  },
  noticeHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    marginBottom: theme.spacing(1),
  },
  noticeBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 8,
    background: '#14688D',
    color: '#fff',
    fontWeight: 700,
    fontSize: 14,
  },
  noticeTitle: {
    color: '#14688D',
    fontWeight: 700,
    margin: 0,
  },
  noticeBody: {
    color: '#34455E',
    lineHeight: 1.5,
    marginTop: theme.spacing(0.5),
  },
  noticeEmphasis: {
    color: '#d32f2f',
    fontWeight: 700,
    margin: 0,
  },
}));

function NumberFormatCustom(props) {
  const { inputRef, onChange, ...other } = props;
  return (
    <NumberFormat
      {...other}
      getInputRef={inputRef}
      onValueChange={values => {
        onChange({ target: { name: props.name, value: values.value } });
      }}
      prefix={'$ '}
      thousandSeparator
      suffix={'.00'}
    />
  );
}

const Review = ({ inputData, navigation }) => {
  const classes = useStyles();
  const { REACT_APP_FORM_DATA_SUBMISSION_API } = process.env;

  const [isFailure, setIsFailure] = useState(false);
  const [isPostSuccess, setIsPostSuccess] = useState(false);

  // Local state for two PDFs
  const [essayFile, setEssayFile] = useState(inputData?.essayFile || null);
  const [tuitionFile, setTuitionFile] = useState(inputData?.tuitionFile || null);

  // Pull values for the tables
  const {
    firstName, lastName, legalStatus, dob, schoolEmail, personalEmail, phone, address, city,
    zipcode, state, school, progress, program, studentId, startYear, grade, militaryAffiliation,
    rent, utilities, tuition, salary, stipend, federal, incomeFromstate, university, other,
    zelle, bankRouting, bankAccount,
  } = inputData;

  const basics = [
    createData('First Name: ', firstName),
    createData('Last Name: ', lastName),
    createData('Legal Status: ', legalStatus),
    createData('Date of Birth: ', dob),
    createData('School Email Address: ', schoolEmail),
    createData('Personal Email Address: ', personalEmail),
    createData('Phone Number: ', phone),
    createData('Address: ', address),
    createData('City: ', city),
    createData('Zip Code: ', zipcode),
    createData('State: ', state),
    createData('University / College: ', school),
    createData('Progress in the Program: ', progress),
    createData('Program: ', program),
    createData('Student ID: ', studentId),
    createData('Year Started: ', startYear),
    createData('Average Grade of Previous Semester: ', grade),
    createData('Military Affiliation: ', militaryAffiliation),
  ];
  const expense = [
    createData('Rent: ', rent),
    createData('Utilities: ', utilities),
    createData('Tuition: ', tuition),
  ];
  const income = [
    createData('Salary', salary),
    createData('Stipend', stipend),
    createData('Grant - Federal', federal),
    createData('Grant - State', incomeFromstate),
    createData('Grant - University', university),
    createData('Other: ', other),
    createData('Zelle: ', zelle),
    createData('Routing Number: ', bankRouting),
    createData('Bank Account Number: ', bankAccount),
  ];

  const { go, next } = navigation;

  // Submit with both files in payload (file1: essay, file2: tuition)
  const postApplication = () => {
    const currentUser = AuthService.getCurrentUser();

    if (!essayFile || !tuitionFile) {
      setIsFailure(true);
      setIsPostSuccess(false);
      return;
    }

    const formData = new FormData();
    const myData = JSON.stringify(inputData);

    formData.append('file1', essayFile);
    formData.append('file2', tuitionFile);
    formData.append('scholarshipDto', myData);

    axios.post(REACT_APP_FORM_DATA_SUBMISSION_API, formData, {
      headers: {
        Authorization: currentUser,
        'Content-Type': 'multipart/form-data',
      },
    })
      .then(function () {
        setIsPostSuccess(true);
        setIsFailure(false);
        next();
      })
      .catch(function (error) {
        console.log('error: ', error);
        setIsFailure(true);
        setIsPostSuccess(false);
      });
  };

  const keepDisabled = () => {
    if (!essayFile || !tuitionFile) return true;
    if (!isPostSuccess) return false;
    return true;
  };

  const nf = useMemo(() => new Intl.NumberFormat(), []);

  // Upload handlers
  const onPickEssay = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type === 'application/pdf') setEssayFile(f);
    // optional: else show small inline message
  };
  const onPickTuition = (e) => {
    const f = e.target.files?.[0];
    if (f && f.type === 'application/pdf') setTuitionFile(f);
  };

  const removeEssay = () => setEssayFile(null);
  const removeTuition = () => setTuitionFile(null);

  const FileLine = ({ title, file, onPick, onRemove, inputId }) => (
    <Box className={classes.uploadCard}>
      <div className={classes.fileMeta}>
        <Typography variant="subtitle1" className={classes.fileName}>
          {title}
        </Typography>
        <Typography variant="body2" className={classes.fileHelper}>
          {file ? file.name : 'PDF only • Max 1 file'}
        </Typography>
      </div>
      <div className={classes.uploadActions}>
        <input
          id={inputId}
          type="file"
          accept="application/pdf"
          style={{ display: 'none' }}
          onChange={onPick}
        />
        <label htmlFor={inputId}>
          <Button variant="contained" className={classes.uploadBtn} component="span">
            Upload
          </Button>
        </label>
        <Button
          variant="outlined"
          className={classes.removeBtn}
          onClick={onRemove}
          disabled={!file}
        >
          Remove
        </Button>
      </div>
    </Box>
  );

  return (
    <TableContainer className={classes.container} component={Paper}>
      <Grid container spacing={1} justify="center">
        <Grid item xs={10} md={4}>
          <Table className={classes.table} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">BASIC INFORMATION</StyledTableCell>
                <StyledTableCell align="left"></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {basics.map((row) => (
                <StyledTableRow key={row.question}>
                  <StyledTableCell align="left">{row.question}</StyledTableCell>
                  <StyledTableCell align="left">{row.answer}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="3vh">
            <Button
              className={classes.reviewButton}
              type="button"
              variant="contained"
              onClick={() => go('Basic Information')}
            >
              Edit Basic Information
            </Button>
          </Box>
        </Grid>

        <Grid item xs={10} md={4}>
          <Table className={classes.table} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">EXPENDITURE (US DOLLARS)</StyledTableCell>
                <StyledTableCell align="left"></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {expense.map((row) => (
                <StyledTableRow key={row.question}>
                  <StyledTableCell align="left">{row.question}</StyledTableCell>
                  <StyledTableCell align="left">{nf.format(row.answer)}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="3vh">
            <Button
              className={classes.reviewButton}
              type="button"
              variant="contained"
              onClick={() => go('Expenditure')}
            >
              Edit Expenditure
            </Button>
          </Box>
        </Grid>

        <Grid item xs={10} md={4}>
          <Table className={classes.table} aria-label="customized table">
            <TableHead>
              <TableRow>
                <StyledTableCell align="left">INCOME (US DOLLARS)</StyledTableCell>
                <StyledTableCell align="left"></StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {income.map((row) => (
                <StyledTableRow key={row.question}>
                  <StyledTableCell align="left">{row.question}</StyledTableCell>
                  <StyledTableCell align="left">{nf.format(row.answer)}</StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="3vh">
            <Button
              className={classes.reviewButton}
              type="button"
              variant="contained"
              onClick={() => go('Income')}
            >
              Edit Income
            </Button>
          </Box>
        </Grid>
      </Grid>

      {/* Essay instructions */}
      <Grid container spacing={3} component="main" className={classes.main}>
        <Grid item xs={1} md={2}></Grid>
        <Grid item xs={10} md={8}>
          <Typography variant="h5" align="center" color="textSecondary" className={classes.hero}>
            Please answer following questions in your essay <br />(1,000 words or less)
          </Typography>
        </Grid>
        <Grid item xs={1} md={2}></Grid>

        <Grid item xs={1} md={2}></Grid>
        <Grid item xs={10} md={8}>
          <Typography variant="h6" align="justify" color="textSecondary" component="p">
            1. Tell us about yourself. (Please write a short autobiography including information about your family,
            work experience, community involvement, hobbies, spare time activities, and what you hope to do in the future.)
          </Typography>
          <Typography variant="h6" align="justify" color="textSecondary" component="p">
            2. What are your academic and/or career goals? (Explain what you want to study, and then what kind of career you want to lead afterwards.)
          </Typography>
          <Typography variant="h6" align="justify" color="textSecondary" component="p">
            3. How will this scholarship help you? (Please answer this by explaining how the award money will help you in at least one of the following ways: financially, professionally, and/or academically.)
          </Typography>
          <Typography variant="h6" align="justify" color="textSecondary" component="p">
            4. Why do you deserve this scholarship? (What differentiates you from other students who apply to our scholarship? Discuss your background, identity, interest, or talent)
          </Typography>
        </Grid>
        <Grid item xs={1} md={2}></Grid>

        <Grid item md={4}></Grid>
        <Grid item md={4}>
          <div className={classes.noticeCard}>
            <div className={classes.noticeHeader}>
              <span className={classes.noticeBadge}>!</span>
              <Typography variant="subtitle1" className={classes.noticeTitle}>
                Important — Upload 2 PDFs
              </Typography>
            </div>

            <Typography variant="body1" className={classes.noticeBody}>
              Please make sure you upload 2 files.
            </Typography>

            <Typography variant="body1" className={classes.noticeEmphasis}>
              Essay and Tuition documents as PDF
            </Typography>

            <Typography variant="body2" className={classes.noticeBody} style={{ marginTop: 8 }}>
              Tuition Document should include your school, student ID, semester and tuition information.
            </Typography>
          </div>
        </Grid>
        <Grid item md={4}></Grid>
      </Grid>

      {/* Two upload lines (Essay & Tuition) */}
      <Grid container spacing={2} style={{ marginTop: 8, marginBottom: 8 }}>
        <Grid item xs={1} md={2}></Grid>
        <Grid item xs={10} md={8}>
          <FileLine
            title="Essay (PDF)"
            file={essayFile}
            onPick={onPickEssay}
            onRemove={removeEssay}
            inputId="essay-file-input"
          />
        </Grid>
        <Grid item xs={1} md={2}></Grid>

        <Grid item xs={1} md={2}></Grid>
        <Grid item xs={10} md={8}>
          <FileLine
            title="Tuition Document (PDF)"
            file={tuitionFile}
            onPick={onPickTuition}
            onRemove={removeTuition}
            inputId="tuition-file-input"
          />
        </Grid>
        <Grid item xs={1} md={2}></Grid>
      </Grid>

      {/* Submit */}
      <Grid container spacing={2}>
        <Grid item xs={1} md={4}></Grid>
        <Grid item xs={10} md={4}>
          {isFailure && (
            <Alert variant="filled" severity="error">
              You should be signed in and uploaded a pdf file to proceed
            </Alert>
          )}
          <Box display="flex" justifyContent="center" alignItems="center" marginTop="1rem">
            <Button
              className={classes.submitButton}
              type="button"
              variant="contained"
              disabled={keepDisabled()}
              onClick={postApplication}
            >
              Submit
            </Button>
          </Box>
        </Grid>
        <Grid item xs={1} md={4}></Grid>
      </Grid>
    </TableContainer>
  );
};

export default Review;
